import { memo, useRef, useEffect, useState, useCallback } from "react";
import { Phone, PhoneOff, Video, VideoOff, Mic, MicOff, X, Clock, Monitor, MonitorOff, Minimize2, Maximize2, GripHorizontal, ChevronDown, ShieldCheck, BatteryWarning, WifiOff, Circle, Download } from "lucide-react";
import ConnectionQualityIndicator from "./ConnectionQualityIndicator";
import RecordingConsentModal from "./RecordingConsentModal";
import { useConnectionQuality } from "@/hooks/useConnectionQuality";
import { useBatteryStatus } from "@/hooks/useBatteryStatus";

interface VideoCallModalProps {
  localStream: MediaStream | null;
  remoteStream: MediaStream | null;
  isConnected: boolean;
  isCalling: boolean;
  isReconnecting: boolean;
  isScreenSharing: boolean;
  isRecording: boolean;
  recordingDuration: number;
  hasRecordingConsent: boolean;
  showConsentRequest: boolean;
  recordedBlob: Blob | null;
  otherUserName: string;
  otherUserPhoto: string | null;
  peerConnection: RTCPeerConnection | null;
  onEndCall: () => void;
  onToggleVideo: () => void;
  onToggleAudio: () => void;
  onToggleScreenShare: () => void;
  onStartRecording: () => void;
  onStopRecording: () => void;
  onRequestConsent: () => void;
  onConsentAccepted: () => void;
  onConsentDeclined: () => void;
}

const formatDuration = (seconds: number): string => {
  const hrs = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;

  if (hrs > 0) {
    return `${hrs}:${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
  }
  return `${mins}:${secs.toString().padStart(2, "0")}`;
};

const VideoCallModal = memo(({
  localStream,
  remoteStream,
  isConnected,
  isCalling,
  isReconnecting,
  isScreenSharing,
  isRecording,
  recordingDuration,
  hasRecordingConsent,
  showConsentRequest,
  recordedBlob,
  otherUserName,
  otherUserPhoto,
  peerConnection,
  onEndCall,
  onToggleVideo,
  onToggleAudio,
  onToggleScreenShare,
  onStartRecording,
  onStopRecording,
  onRequestConsent,
  onConsentAccepted,
  onConsentDeclined,
}: VideoCallModalProps) => {
  const localVideoRef = useRef<HTMLVideoElement>(null);
  const remoteVideoRef = useRef<HTMLVideoElement>(null);
  const pipVideoRef = useRef<HTMLVideoElement>(null);
  const pipContainerRef = useRef<HTMLDivElement>(null);
  const [isVideoEnabled, setIsVideoEnabled] = useState(true);
  const [isAudioEnabled, setIsAudioEnabled] = useState(true);
  const [callDuration, setCallDuration] = useState(0);
  const [isPiPMode, setIsPiPMode] = useState(false);
  const [pipPosition, setPipPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [showSwipeHint, setShowSwipeHint] = useState(true);
  const dragStartRef = useRef({ x: 0, y: 0, posX: 0, posY: 0 });
  const lastTapRef = useRef<number>(0);
  const swipeStartRef = useRef<{ y: number; time: number } | null>(null);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const swipeHintTimerRef = useRef<NodeJS.Timeout | null>(null);
  
  const connectionStats = useConnectionQuality(peerConnection);
  const batteryStatus = useBatteryStatus();

  // Auto-hide swipe hint after 3 seconds
  useEffect(() => {
    if (isConnected && !isPiPMode && showSwipeHint) {
      swipeHintTimerRef.current = setTimeout(() => {
        setShowSwipeHint(false);
      }, 3000);
    }

    return () => {
      if (swipeHintTimerRef.current) {
        clearTimeout(swipeHintTimerRef.current);
      }
    };
  }, [isConnected, isPiPMode, showSwipeHint]);

  // Reset swipe hint when returning to fullscreen
  useEffect(() => {
    if (!isPiPMode) {
      setShowSwipeHint(true);
    }
  }, [isPiPMode]);

  // Call duration timer
  useEffect(() => {
    if (isConnected) {
      setCallDuration(0);
      timerRef.current = setInterval(() => {
        setCallDuration((prev) => prev + 1);
      }, 1000);
    } else {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    }

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [isConnected]);

  useEffect(() => {
    if (localVideoRef.current && localStream) {
      localVideoRef.current.srcObject = localStream;
    }
  }, [localStream]);

  useEffect(() => {
    if (remoteVideoRef.current && remoteStream) {
      remoteVideoRef.current.srcObject = remoteStream;
    }
    if (pipVideoRef.current && remoteStream) {
      pipVideoRef.current.srcObject = remoteStream;
    }
  }, [remoteStream]);

  const handleToggleVideo = () => {
    setIsVideoEnabled(!isVideoEnabled);
    onToggleVideo();
  };

  const handleToggleAudio = () => {
    setIsAudioEnabled(!isAudioEnabled);
    onToggleAudio();
  };

  const handleToggleRecording = () => {
    if (isRecording) {
      onStopRecording();
    } else if (hasRecordingConsent) {
      onStartRecording();
    } else {
      onRequestConsent();
    }
  };

  const handleDownloadRecording = () => {
    if (!recordedBlob) return;
    
    const url = URL.createObjectURL(recordedBlob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `call-recording-${new Date().toISOString().slice(0, 19).replace(/:/g, "-")}.webm`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    
    if ("vibrate" in navigator) {
      navigator.vibrate(50);
    }
  };

  const togglePiPMode = () => {
    setIsPiPMode(!isPiPMode);
    // Reset position when entering PiP mode
    if (!isPiPMode) {
      setPipPosition({ x: 0, y: 0 });
    }
  };

  // Drag handlers for PiP
  const handleDragStart = useCallback((e: React.PointerEvent) => {
    e.preventDefault();
    setIsDragging(true);
    dragStartRef.current = {
      x: e.clientX,
      y: e.clientY,
      posX: pipPosition.x,
      posY: pipPosition.y,
    };
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
  }, [pipPosition]);

  const handleDragMove = useCallback((e: React.PointerEvent) => {
    if (!isDragging) return;
    
    const deltaX = e.clientX - dragStartRef.current.x;
    const deltaY = e.clientY - dragStartRef.current.y;
    
    // Calculate new position (negative because we're offsetting from bottom-right)
    let newX = dragStartRef.current.posX - deltaX;
    let newY = dragStartRef.current.posY - deltaY;
    
    // Constrain to viewport bounds
    const pipWidth = 160; // w-40 = 10rem = 160px
    const pipHeight = 156; // approximate height
    const padding = 16;
    
    const maxX = window.innerWidth - pipWidth - padding;
    const maxY = window.innerHeight - pipHeight - padding;
    
    newX = Math.max(-maxX + padding, Math.min(0, newX));
    newY = Math.max(-maxY + padding, Math.min(0, newY));
    
    setPipPosition({ x: newX, y: newY });
  }, [isDragging]);

  const handleDragEnd = useCallback((e: React.PointerEvent) => {
    setIsDragging(false);
    (e.target as HTMLElement).releasePointerCapture(e.pointerId);

    // Snap to nearest corner
    const pipWidth = 160;
    const pipHeight = 156;
    const padding = 16;
    const bottomNavHeight = 80;

    // Calculate current center position of PiP
    const currentRight = 16 - pipPosition.x;
    const currentBottom = bottomNavHeight - pipPosition.y;
    const centerX = window.innerWidth - currentRight - pipWidth / 2;
    const centerY = window.innerHeight - currentBottom - pipHeight / 2;

    // Define corner positions (as center points)
    const corners = [
      { name: "top-left", x: padding + pipWidth / 2, y: padding + pipHeight / 2 },
      { name: "top-right", x: window.innerWidth - padding - pipWidth / 2, y: padding + pipHeight / 2 },
      { name: "bottom-left", x: padding + pipWidth / 2, y: window.innerHeight - bottomNavHeight - pipHeight / 2 },
      { name: "bottom-right", x: window.innerWidth - padding - pipWidth / 2, y: window.innerHeight - bottomNavHeight - pipHeight / 2 },
    ];

    // Find nearest corner
    let nearestCorner = corners[3]; // default to bottom-right
    let minDistance = Infinity;

    corners.forEach((corner) => {
      const distance = Math.sqrt(
        Math.pow(centerX - corner.x, 2) + Math.pow(centerY - corner.y, 2)
      );
      if (distance < minDistance) {
        minDistance = distance;
        nearestCorner = corner;
      }
    });

    // Calculate new position based on nearest corner
    let newX = 0;
    let newY = 0;

    if (nearestCorner.name === "top-left") {
      newX = -(window.innerWidth - pipWidth - padding * 2);
      newY = -(window.innerHeight - pipHeight - bottomNavHeight - padding);
    } else if (nearestCorner.name === "top-right") {
      newX = 0;
      newY = -(window.innerHeight - pipHeight - bottomNavHeight - padding);
    } else if (nearestCorner.name === "bottom-left") {
      newX = -(window.innerWidth - pipWidth - padding * 2);
      newY = 0;
    } else {
      // bottom-right (default)
      newX = 0;
      newY = 0;
    }

    setPipPosition({ x: newX, y: newY });

    // Haptic feedback on snap
    if ("vibrate" in navigator) {
      navigator.vibrate(10);
    }
  }, [pipPosition]);

  // Double-tap handler for PiP
  const handleDoubleTap = useCallback(() => {
    const now = Date.now();
    const DOUBLE_TAP_DELAY = 300; // ms

    if (now - lastTapRef.current < DOUBLE_TAP_DELAY) {
      // Double tap detected - expand to fullscreen
      if ("vibrate" in navigator) {
        navigator.vibrate(15);
      }
      setIsPiPMode(false);
      lastTapRef.current = 0;
    } else {
      lastTapRef.current = now;
    }
  }, []);

  // Swipe down gesture handlers for fullscreen view
  const handleSwipeStart = useCallback((e: React.TouchEvent) => {
    swipeStartRef.current = {
      y: e.touches[0].clientY,
      time: Date.now(),
    };
  }, []);

  const handleSwipeEnd = useCallback((e: React.TouchEvent) => {
    if (!swipeStartRef.current || !isConnected) return;

    const endY = e.changedTouches[0].clientY;
    const deltaY = endY - swipeStartRef.current.y;
    const deltaTime = Date.now() - swipeStartRef.current.time;

    // Detect swipe down: minimum 100px movement, within 300ms, downward direction
    const SWIPE_THRESHOLD = 100;
    const TIME_THRESHOLD = 300;

    if (deltaY > SWIPE_THRESHOLD && deltaTime < TIME_THRESHOLD) {
      // Swipe down detected - minimize to PiP
      if ("vibrate" in navigator) {
        navigator.vibrate(15);
      }
      setPipPosition({ x: 0, y: 0 });
      setIsPiPMode(true);
    }

    swipeStartRef.current = null;
  }, [isConnected]);

  // Picture-in-Picture mini view
  if (isPiPMode) {
    return (
      <div 
        ref={pipContainerRef}
        className="fixed z-[60] w-40 rounded-2xl overflow-hidden shadow-2xl border-2 border-primary/50 bg-black select-none transition-all duration-300 ease-out"
        style={{
          bottom: `${80 - pipPosition.y}px`,
          right: `${16 - pipPosition.x}px`,
        }}
        onClick={handleDoubleTap}
      >
        {/* Drag handle */}
        <div
          className={`absolute top-0 left-0 right-0 h-6 flex items-center justify-center cursor-grab z-10 bg-gradient-to-b from-black/60 to-transparent ${isDragging ? "cursor-grabbing" : ""}`}
          onPointerDown={handleDragStart}
          onPointerMove={handleDragMove}
          onPointerUp={handleDragEnd}
          onPointerCancel={handleDragEnd}
        >
          <GripHorizontal className="w-4 h-4 text-white/50" />
        </div>
        {/* Remote video */}
        {isConnected && remoteStream ? (
          <video
            ref={pipVideoRef}
            autoPlay
            playsInline
            className="w-full h-28 object-cover"
          />
        ) : (
          <div className="w-full h-28 flex items-center justify-center bg-muted">
            {otherUserPhoto ? (
              <img src={otherUserPhoto} alt={otherUserName} className="w-12 h-12 rounded-full object-cover" />
            ) : (
              <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center text-lg font-bold text-primary">
                {otherUserName?.[0]}
              </div>
            )}
          </div>
        )}

        {/* Duration overlay */}
        {isConnected && (
          <div className="absolute top-2 left-2 flex items-center gap-1 px-2 py-0.5 rounded-full bg-black/60 backdrop-blur-sm">
            <Clock className="w-3 h-3 text-white/70" />
            <span className="text-xs font-medium text-white tabular-nums">
              {formatDuration(callDuration)}
            </span>
          </div>
        )}

        {/* Mini controls */}
        <div className="flex items-center justify-between p-2 bg-black/80">
          <div className="flex items-center gap-1">
            <button
              onClick={handleToggleAudio}
              className={`p-1.5 rounded-full transition-colors ${
                isAudioEnabled ? "bg-white/20 text-white" : "bg-red-500 text-white"
              }`}
            >
              {isAudioEnabled ? <Mic className="w-4 h-4" /> : <MicOff className="w-4 h-4" />}
            </button>
            <button
              onClick={handleToggleVideo}
              className={`p-1.5 rounded-full transition-colors ${
                isVideoEnabled ? "bg-white/20 text-white" : "bg-red-500 text-white"
              }`}
            >
              {isVideoEnabled ? <Video className="w-4 h-4" /> : <VideoOff className="w-4 h-4" />}
            </button>
          </div>

          <div className="flex items-center gap-1">
            <button
              onClick={togglePiPMode}
              className="p-1.5 rounded-full bg-white/20 text-white hover:bg-white/30 transition-colors"
            >
              <Maximize2 className="w-4 h-4" />
            </button>
            <button
              onClick={onEndCall}
              className="p-1.5 rounded-full bg-red-500 text-white hover:bg-red-600 transition-colors"
            >
              <PhoneOff className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Full screen view
  return (
    <div 
      className="fixed inset-0 z-[60] bg-black flex flex-col"
      onTouchStart={handleSwipeStart}
      onTouchEnd={handleSwipeEnd}
    >
      {/* Swipe down indicator */}
      {isConnected && (
        <div 
          className={`absolute top-0 left-0 right-0 z-20 flex flex-col items-center pt-2 pointer-events-none transition-opacity duration-500 ${
            showSwipeHint ? "opacity-100" : "opacity-0"
          }`}
        >
          <div className="w-10 h-1 rounded-full bg-white/40 mb-1" />
          <div className="flex items-center gap-1 text-white/40 animate-bounce">
            <ChevronDown className="w-4 h-4" />
            <span className="text-[10px] font-medium">Swipe to minimize</span>
            <ChevronDown className="w-4 h-4" />
          </div>
        </div>
      )}

      {/* Remote video (full screen) */}
      {isConnected && remoteStream ? (
        <video
          ref={remoteVideoRef}
          autoPlay
          playsInline
          className="w-full h-full object-cover"
        />
      ) : (
        <div className="flex-1 flex flex-col items-center justify-center">
          <div className="w-24 h-24 rounded-full overflow-hidden bg-muted mb-4">
            {otherUserPhoto ? (
              <img src={otherUserPhoto} alt={otherUserName} className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-3xl font-bold text-muted-foreground">
                {otherUserName?.[0]}
              </div>
            )}
          </div>
          <h2 className="text-white text-xl font-semibold mb-2">{otherUserName}</h2>
          <p className="text-white/70 animate-pulse">
            {isCalling ? "Calling..." : "Connecting..."}
          </p>
        </div>
      )}

      {/* Reconnecting overlay */}
      {isReconnecting && (
        <div className="absolute inset-0 bg-black/80 flex flex-col items-center justify-center z-30">
          <div className="relative mb-6">
            <WifiOff className="w-16 h-16 text-yellow-500 animate-pulse" />
            <div className="absolute -bottom-1 -right-1 w-6 h-6 rounded-full bg-yellow-500 flex items-center justify-center">
              <div className="w-4 h-4 rounded-full border-2 border-black border-t-transparent animate-spin" />
            </div>
          </div>
          <h3 className="text-white text-xl font-semibold mb-2">Connection Lost</h3>
          <p className="text-white/70 text-center mb-4 px-8">
            Attempting to reconnect...
          </p>
          <div className="flex items-center gap-1">
            <div className="w-2 h-2 rounded-full bg-yellow-500 animate-bounce" style={{ animationDelay: "0ms" }} />
            <div className="w-2 h-2 rounded-full bg-yellow-500 animate-bounce" style={{ animationDelay: "150ms" }} />
            <div className="w-2 h-2 rounded-full bg-yellow-500 animate-bounce" style={{ animationDelay: "300ms" }} />
          </div>
          <button
            onClick={onEndCall}
            className="mt-8 px-6 py-2 rounded-full bg-red-500 text-white text-sm font-medium hover:bg-red-600 transition-colors"
          >
            End Call
          </button>
        </div>
      )}

      {/* Local video (picture-in-picture) */}
      <div className="absolute top-4 right-4 w-32 h-44 rounded-xl overflow-hidden bg-muted shadow-lg">
        <video
          ref={localVideoRef}
          autoPlay
          playsInline
          muted
          className="w-full h-full object-cover"
        />
        {!isVideoEnabled && !isScreenSharing && (
          <div className="absolute inset-0 bg-muted flex items-center justify-center">
            <VideoOff className="w-8 h-8 text-muted-foreground" />
          </div>
        )}
        {isScreenSharing && (
          <div className="absolute bottom-1 left-1 right-1 bg-primary/80 text-white text-[10px] text-center py-0.5 rounded">
            Sharing screen
          </div>
        )}
      </div>

      {/* Close and minimize buttons */}
      <div className="absolute top-4 left-4 flex items-center gap-2">
        <button
          onClick={onEndCall}
          className="p-2 rounded-full bg-black/50 text-white hover:bg-black/70 transition-colors"
        >
          <X className="w-6 h-6" />
        </button>
        {isConnected && (
          <button
            onClick={togglePiPMode}
            className="p-2 rounded-full bg-black/50 text-white hover:bg-black/70 transition-colors"
          >
            <Minimize2 className="w-6 h-6" />
          </button>
        )}
      </div>

      {/* Connection quality indicator, duration, and encryption */}
      {(isConnected || isCalling) && (
        <div className="absolute top-4 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2">
          <ConnectionQualityIndicator quality={connectionStats.quality} />
          {isConnected && (
            <>
              <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-black/50 backdrop-blur-sm">
                <Clock className="w-3.5 h-3.5 text-white/70" />
                <span className="text-sm font-medium text-white tabular-nums">
                  {formatDuration(callDuration)}
                </span>
              </div>
              <div className="flex items-center gap-1 px-2.5 py-1 rounded-full bg-green-500/20 backdrop-blur-sm border border-green-500/30">
                <ShieldCheck className="w-3 h-3 text-green-400" />
                <span className="text-[10px] font-medium text-green-400">End-to-end encrypted</span>
              </div>
            </>
          )}
        </div>
      )}

      {/* Low battery warning */}
      {isConnected && batteryStatus.supported && batteryStatus.isLow && (
        <div 
          className={`absolute bottom-24 left-1/2 -translate-x-1/2 flex items-center gap-2 px-4 py-2 rounded-full backdrop-blur-sm animate-pulse ${
            batteryStatus.isCritical 
              ? "bg-red-500/80 border border-red-400" 
              : "bg-yellow-500/80 border border-yellow-400"
          }`}
        >
          <BatteryWarning className={`w-5 h-5 ${batteryStatus.isCritical ? "text-white" : "text-yellow-900"}`} />
          <span className={`text-sm font-medium ${batteryStatus.isCritical ? "text-white" : "text-yellow-900"}`}>
            {batteryStatus.isCritical 
              ? `Critical battery: ${batteryStatus.level}%` 
              : `Low battery: ${batteryStatus.level}%`}
          </span>
        </div>
      )}

      {/* Recording Consent Modal */}
      <RecordingConsentModal
        isOpen={showConsentRequest}
        otherUserName={otherUserName}
        onAccept={onConsentAccepted}
        onDecline={onConsentDeclined}
      />

      {/* Recording indicator */}
      {isRecording && (
        <div className="absolute top-20 left-1/2 -translate-x-1/2 flex items-center gap-2 px-4 py-2 rounded-full bg-red-500/90 backdrop-blur-sm">
          <Circle className="w-3 h-3 text-white fill-white animate-pulse" />
          <span className="text-sm font-medium text-white">
            REC {formatDuration(recordingDuration)}
          </span>
        </div>
      )}

      {/* Download recording button */}
      {recordedBlob && !isRecording && (
        <div className="absolute top-20 left-1/2 -translate-x-1/2">
          <button
            onClick={handleDownloadRecording}
            className="flex items-center gap-2 px-4 py-2 rounded-full bg-green-500/90 backdrop-blur-sm hover:bg-green-600/90 transition-colors"
          >
            <Download className="w-4 h-4 text-white" />
            <span className="text-sm font-medium text-white">Save Recording</span>
          </button>
        </div>
      )}

      {/* Controls */}
      <div className="absolute bottom-8 left-0 right-0 flex justify-center gap-3">
        <button
          onClick={handleToggleAudio}
          className={`p-4 rounded-full transition-colors ${
            isAudioEnabled ? "bg-white/20 text-white" : "bg-red-500 text-white"
          }`}
        >
          {isAudioEnabled ? <Mic className="w-6 h-6" /> : <MicOff className="w-6 h-6" />}
        </button>

        <button
          onClick={handleToggleVideo}
          className={`p-4 rounded-full transition-colors ${
            isVideoEnabled ? "bg-white/20 text-white" : "bg-red-500 text-white"
          }`}
        >
          {isVideoEnabled ? <Video className="w-6 h-6" /> : <VideoOff className="w-6 h-6" />}
        </button>

        {isConnected && (
          <>
            <button
              onClick={onToggleScreenShare}
              className={`p-4 rounded-full transition-colors ${
                isScreenSharing ? "bg-primary text-white" : "bg-white/20 text-white"
              }`}
            >
              {isScreenSharing ? <MonitorOff className="w-6 h-6" /> : <Monitor className="w-6 h-6" />}
            </button>

            <button
              onClick={handleToggleRecording}
              className={`p-4 rounded-full transition-colors ${
                isRecording ? "bg-red-500 text-white animate-pulse" : "bg-white/20 text-white"
              }`}
              title={isRecording ? "Stop recording" : "Start recording"}
            >
              <Circle className={`w-6 h-6 ${isRecording ? "fill-white" : ""}`} />
            </button>
          </>
        )}

        <button
          onClick={onEndCall}
          className="p-4 rounded-full bg-red-500 text-white hover:bg-red-600 transition-colors"
        >
          <PhoneOff className="w-6 h-6" />
        </button>
      </div>
    </div>
  );
});

VideoCallModal.displayName = "VideoCallModal";

export default VideoCallModal;
